import * as React from "react";
import PhoneInputPrimitive, {
  Country,
  getCountryCallingCode,
  formatPhoneNumber,
  formatPhoneNumberIntl,
  isValidPhoneNumber as isValidPhoneNumberLib,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

// Re-export validation function
export { isValidPhoneNumber as isValidPhoneNumber } from "react-phone-number-input";
export { formatPhoneNumberIntl, formatPhoneNumber };

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value"
> & {
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: Country;
  error?: boolean;
};

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value, onChange, defaultCountry = "PT", error, ...props }, ref) => {
    // Inline forwardRef on `inputComponent` would create a new component
    // identity each render, remounting the underlying <input> and killing
    // focus between keystrokes. Memoize by [error] to keep identity stable.
    const MemoInputComponent = React.useMemo(
      () =>
        React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
          (inputProps, inputRef) => (
            <InputComponent {...inputProps} ref={inputRef} error={error} />
          )
        ),
      [error]
    );

    const handleChange = React.useCallback(
      (val: string | undefined) => onChange?.(val || ''),
      [onChange]
    );

    return (
      <PhoneInputPrimitive
        className={cn("flex", className)}
        international
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        inputComponent={MemoInputComponent}
        value={value}
        onChange={handleChange}
        {...props}
      />
    );
  }
);
PhoneInput.displayName = "PhoneInput";

// Flag Component
interface FlagProps {
  country: Country;
  countryName: string;
}

const FlagComponent = ({ country, countryName }: FlagProps) => {
  const Flag = flags[country];
  return (
    <span
      className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/10"
      title={countryName}
    >
      {Flag ? <Flag title={countryName} /> : <span className="text-xs">🏳️</span>}
    </span>
  );
};

// Input Component
interface InputComponentProps extends React.ComponentProps<"input"> {
  error?: boolean;
}

const InputComponent = React.forwardRef<HTMLInputElement, InputComponentProps>(
  ({ className, error, ...props }, ref) => (
    <Input
      className={cn(
        "rounded-s-none rounded-e-md",
        error && "border-destructive focus-visible:ring-destructive",
        className
      )}
      {...props}
      ref={ref}
    />
  )
);
InputComponent.displayName = "InputComponent";

// Country Select Component
interface CountrySelectOption {
  label: string;
  value: Country;
}

interface CountrySelectProps {
  disabled?: boolean;
  value: Country;
  onChange: (value: Country) => void;
  options: CountrySelectOption[];
}

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
}: CountrySelectProps) => {
  const [open, setOpen] = React.useState(false);

  const handleSelect = React.useCallback(
    (country: Country) => {
      onChange(country);
      setOpen(false);
    },
    [onChange]
  );

  const selectedOption = options.find((opt) => opt.value === value);
  const Flag = value ? flags[value] : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "flex gap-1 rounded-e-none rounded-s-md border-r-0 px-3 focus:z-10",
            disabled && "cursor-not-allowed opacity-50"
          )}
          disabled={disabled}
        >
          <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/10">
            {Flag ? <Flag title={selectedOption?.label} /> : <span className="text-xs">🏳️</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Pesquisar país..." />
          <CommandList>
            <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="h-72">
                {options
                  .filter((opt) => opt.value)
                  .map((option) => {
                    const OptionFlag = flags[option.value];
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => handleSelect(option.value)}
                        className="gap-2"
                      >
                        <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/10">
                          {OptionFlag ? (
                            <OptionFlag title={option.label} />
                          ) : (
                            <span className="text-xs">🏳️</span>
                          )}
                        </span>
                        <span className="flex-1 text-sm">{option.label}</span>
                        <span className="text-xs text-muted-foreground">
                          +{getCountryCallingCode(option.value)}
                        </span>
                        {option.value === value && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </CommandItem>
                    );
                  })}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
